<script lang="ts">
  import PriceRating from "component_library/components/svelte/PriceRating.svelte"
  import AUText from "component_library/components/svelte/ui/AUText.svelte"
  import { getContext, onDestroy } from "svelte"
  import { Settings } from "website_script/types/settings"
  import Svg from "./Svg.svelte"
  import logoBadge from "website_script/assets/images/logo-badge.png"
  import infoFilled from "icons/infoFilled.svg?raw"
  import { queryStore, getContextClient } from "@urql/svelte"
  import { getDealerThemeTokensStyle } from "website_script/lib/dealerTheme"
  import { fly } from "svelte/transition"
  import { crossfade } from "svelte/transition"
  import { Environment } from "website_script/types/environment"
  import type { Texts } from "website_script/types/texts"
  import { ButtonConfig } from "website_script/types/widgets"
  import { GET_CAR_HIGHLIGHTS_FOR_WIDGET } from "website_script/operations/getCarHighlightsForWidget"
  import ConversionBoxInsightsDetails from "./ConversionBoxInsightsDetails.svelte"
  import AUButton from "component_library/components/svelte/ui/AUButton.svelte"
  import Skeleton from "./Skeleton.svelte"
  import ConversionBoxInsightsLayout from "./ConversionBoxInsightsLayout.svelte"
  import { autouncleBrandTheme } from "lib/constants"
  import AUStack from "component_library/components/svelte/ui/AUStack.svelte"
  import AUBrandText from "component_library/components/svelte/ui/AUBrandText.svelte"
  import checkCircle from "@autouncle/icons/dist/assets/check-circle.svg?raw"

  export let car: NonNullable<Settings["car"]>
  export let buttons: ButtonConfig[]

  const { trackFunction } = getContext<Environment>("environment")
  const { conversionBox } = getContext<Texts>("texts")
  const { carRatingText } = getContext<Settings>("settings")

  const carId = car.id
  // Currently in the ConversionBox it only makes sense to show positive price ratings
  const rating = car.au_rating == null || car.au_rating < 3 ? 0 : car.au_rating
  const ratingText = carRatingText ?? ""

  let intervalId: ReturnType<typeof setInterval> | null = null
  let currentHighlightIndex = 0
  let areHighlightsTracked = false

  const client = getContextClient()
  const carHighlights = queryStore({
    client,
    query: GET_CAR_HIGHLIGHTS_FOR_WIDGET,
    requestPolicy: "cache-and-network",
    variables: {
      carId: carId ? String(carId) : "",
    },
  })

  const trackCarHighlights = (details: string) => {
    trackFunction({
      entity: "conversion-box",
      element: "insights",
      action: "displayed",
      details,
      type: "micro_proceeding",
      carRating: rating,
    })
    areHighlightsTracked = true
  }

  $: carHighlightsLength =
    $carHighlights.data?.conversionBoxCarHighlights?.highlights?.length ?? 0

  $: carHighlightsForWidget =
    $carHighlights.data?.conversionBoxCarHighlights?.highlights ?? []
  $: insightsBasisText =
    $carHighlights.data?.conversionBoxCarHighlights?.insightsBasisText ?? ""

  $: {
    $carHighlights.data &&
      !areHighlightsTracked &&
      trackCarHighlights(
        $carHighlights.data.conversionBoxCarHighlights?.highlights
          ?.map((h) => h.name)
          .join(", ") || "no highlights"
      )
  }

  $: {
    // Do this once, when the data comes in
    if (intervalId === null && !$carHighlights.stale) {
      currentHighlightIndex = 0
      intervalId = setInterval(() => {
        currentHighlightIndex =
          currentHighlightIndex === carHighlightsLength - 1
            ? 0
            : currentHighlightIndex + 1
      }, 5000)
    }
  }

  const dealerThemeTokensStyle = getDealerThemeTokensStyle(autouncleBrandTheme)

  $: insightsBoxStyle = dealerThemeTokensStyle.concat(`
    ${carHighlightsLength ? "cursor: pointer;" : ""}
  `)

  onDestroy(() => {
    intervalId !== null && clearInterval(intervalId)
  })

  const [send, receive] = crossfade({
    duration: 300,
    fallback(node) {
      return fly(node, { duration: 300, y: 20 })
    },
  })

  let conversionBoxInsightsDetailsComponent: ConversionBoxInsightsDetails

  const handleInsightsOpen = () => {
    if (conversionBoxInsightsDetailsComponent?.openModal) {
      conversionBoxInsightsDetailsComponent.openModal()
    }
  }

  $: auInsightsBoxAriaProps = carHighlightsLength
    ? { role: "button", tabindex: 0 }
    : {}
</script>

<!--
@component
An AutoUncle themed Insights Box.

- the car title shows an ellipsis when it is too long
- the car highlights have a fixed height and line clamp to make sure they are consistent in their layout
- when there is only one highlight, there is no fixed height or line clamp
- when there are no highlights (or there is an error), we show fallback content
-->
{#if $carHighlights.fetching}
  <ConversionBoxInsightsLayout style={insightsBoxStyle}>
    <svelte:fragment slot="featured">
      <AUStack
        layout="horizontal"
        gap="md"
        alignItems="center"
        wrap="nowrap"
        style="width: 100%;"
      >
        <div>
          <Skeleton width="48px" height="56px" />
        </div>

        <div style="width: 100%;">
          <Skeleton width="85%" padding="5px" />
          <Skeleton width="40%" padding="5px" />
        </div>
      </AUStack>
    </svelte:fragment>
    <div slot="highlights" class="au-car-highlight">
      <Skeleton width="100%" padding="5px" />
      <Skeleton width="100%" padding="5px" />
      <Skeleton width="50%" padding="5px" />
    </div>
    <Skeleton slot="actions" width="39%" height="40px" />
  </ConversionBoxInsightsLayout>
{:else if $carHighlights.error || !carHighlightsLength}
  <ConversionBoxInsightsLayout showHeader={false} style={insightsBoxStyle}>
    <div slot="content">
      <AUText font="body-small" color="default">
        <Svg
          class="au-car-highlight__title-icon"
          aria-hidden="true"
          data={checkCircle}
          color="#56ac82"
          size="16"
        />
        <strong>{conversionBox.noHighlightsTitle}</strong>
        {conversionBox.noHighlightsText}
      </AUText>
    </div>
  </ConversionBoxInsightsLayout>
{:else}
  <ConversionBoxInsightsLayout
    style={insightsBoxStyle}
    {...auInsightsBoxAriaProps}
    onClick={carHighlightsLength ? handleInsightsOpen : undefined}
    onKeydown={(e) =>
      carHighlightsLength && e.key === "Enter" && handleInsightsOpen()}
    data-highlights-no={carHighlightsLength}
  >
    <svelte:fragment slot="featured">
      {#if rating && ratingText}
        <AUStack
          layout="horizontal"
          gap="md"
          alignItems="center"
          wrap="nowrap"
          style="max-width: 100%;"
        >
          <div>
            <img
              src={logoBadge}
              class="au-logo-badge"
              alt="AutoUncle logo in a badge"
            />
          </div>

          <div class="au-price-rating-wrapper">
            <AUText ellipsis={true} font="body-small" color="default"
              >{car.brand} {car.car_model} {car.variant}</AUText
            >
            <PriceRating layout="horizontal" {rating} {ratingText} />
          </div>
        </AUStack>
      {:else}
        <AUText ellipsis={true} font="body-small" color="default"
          >{car.brand} {car.car_model} {car.variant}</AUText
        >
      {/if}
    </svelte:fragment>

    <svelte:fragment slot="highlights">
      {#each carHighlightsForWidget as carHighlight, i (carHighlight.title)}
        {#if i === currentHighlightIndex}
          <div
            class="au-car-highlight"
            in:receive={{ key: carHighlight.title }}
            out:send={{ key: carHighlight.title }}
          >
            <AUText lineClamp={true} font="body-small" color="default">
              <Svg
                class="au-car-highlight__title-icon"
                aria-hidden="true"
                data={checkCircle}
                color="#56ac82"
                size="16"
              />
              {#if carHighlight.title}
                <strong><AUBrandText>{carHighlight.title}:</AUBrandText></strong
                >
              {/if}
              {carHighlight.text}
            </AUText>
          </div>
        {/if}
      {/each}
    </svelte:fragment>

    <AUButton
      slot="actions"
      color="secondary"
      allowIconOwnColor={true}
      onClick={() => undefined}
      ><Svg
        alt={conversionBox.learnMore}
        aria-hidden="true"
        data={infoFilled}
        slot="leftIcon"
      />
      <AUBrandText>{conversionBox.learnMore}</AUBrandText></AUButton
    >
  </ConversionBoxInsightsLayout>
{/if}

<ConversionBoxInsightsDetails
  bind:this={conversionBoxInsightsDetailsComponent}
  {car}
  {carHighlightsForWidget}
  {buttons}
  {insightsBasisText}
/>

<style>
  .au-car-highlight {
    position: absolute;
    width: 100%;
  }

  :global([data-highlights-no="1"]) .au-car-highlight {
    position: initial;
  }

  :global([data-highlights-no="1"] .au-line-clamp) {
    overflow: initial;
    line-clamp: initial;
    -webkit-line-clamp: initial;
  }

  :global(.au-car-highlight__title-icon) {
    display: inline-block;
    vertical-align: text-top;
  }

  .au-logo-badge {
    display: block;
    height: 56px;
    width: auto;
    /* style leakage fix */
    max-width: none;
  }

  .au-price-rating-wrapper {
    min-width: 0;
    max-width: 100%;
  }

  :global(.au-compact-logo) {
    display: block;

    @container (min-width: 360px) {
      display: none;
    }
  }

  :global(.au-full-logo) {
    display: none;

    @container (min-width: 360px) {
      display: block;
    }
  }
</style>
